import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ResponsiveContainer,
    AreaChart, Area,
} from 'recharts';
// import { Modal, Button } from 'react-bootstrap';
import SurveyComments from '../components/SurveyComments';
import ProgressBar from '../components/ProgressBar';
import '../css/survey.css';
import '../css/pagination.css';
import LoadingSpinner from "../components/LoadingSpinner";
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faPoll } from "@fortawesome/free-solid-svg-icons";
import api from "../api";
import { AuthContext } from "../context/AuthContext";

function SurveyPage() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [userId, setUserId] = useState(null);
    const [isVoted, setIsVoted] = useState(false);
    const [isCommented, setIsCommented] = useState(false);
    const [survey, setSurvey] = useState({ questions: [] });
    const [selectedOptions, setSelectedOptions] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    // const [showModal, setShowModal] = useState(false);
    const [loadingVote, setLoadingVote] = useState(false);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const accessToken = localStorage.getItem('access_token');
    const { isAuthenticated, isAuthReady, isAdmin, isBanned, bannedEndDate } = useContext(AuthContext);
    const chartTypes = ['Bar', 'Area'];
    const [chartType, setChartType] = useState('Bar');

    const getXAxisProps = (questionData) => {
        if (questionData.length > 5) {
            return {
                tick: { angle: -45, textAnchor: 'end' },
                interval: 0
            };
        }
        return {};
    };

    const fetchSurveyData = async () => {
        setIsLoading(true);
        try {
            const surveyResponse = await api.get(`${process.env.REACT_APP_API_URL}/api/survey?lang=${i18n.language}`);
            const updatedQuestions = surveyResponse.data.questions.map(question => ({
                ...question,
                options: question.options.map((opt, index) => ({
                    ...opt,
                    logicalGroup: index + 1,
                })),
            }));
            setSurvey({ ...surveyResponse.data, questions: updatedQuestions });

            if (isAuthenticated) {
                try {
                    const userInfoResponse = await api.get(`${process.env.REACT_APP_API_URL}/api/survey/user-info`, {
                        headers: { Authorization: `Bearer ${accessToken}` },
                    });
                    setIsVoted(userInfoResponse.data.voted);
                    setIsCommented(userInfoResponse.data.commented);
                    setUserId(userInfoResponse.data.userId);
                } catch (error) {
                    Swal.fire({
                        text: t('survey.errorSubmittingVotes'),
                        icon: 'error',
                        background: '#1a1a1a',
                        color: '#b3b3b3',
                        iconColor: '#ff0000',
                        confirmButtonColor: '#3085d6',
                        confirmButtonText: t('survey.ok'),
                        customClass: {
                            popup: 'swal2-dark-popup',
                            confirmButton: 'swal2-dark-button'
                        }
                    });
                }
            }
        } catch (error) {

        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isAuthReady) {
            setSelectedOptions({});
            setCurrentQuestionIndex(0);
            fetchSurveyData();
        }
    }, [isAuthReady, i18n.language]);

    const handleVote = async () => {
        if (!isAuthenticated) {
            Swal.fire({
                text: t('survey.loginRequired'),
                icon: 'warning',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ffcc00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('survey.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            return;
        }

        if (Object.keys(selectedOptions).length !== survey.questions.length) {
            Swal.fire({
                text: t('survey.completeAllQuestions'),
                icon: 'warning',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ffcc00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('survey.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            return;
        }

        const payload = survey.questions.map(question => ({
            questionId: question.questionId,
            groupId: question.groupId,
            optionId: selectedOptions[question.questionId],
            logicalGroup: question.options.find(opt => opt.id === selectedOptions[question.questionId])?.logicalGroup || null,
        }));

        setLoadingVote(true);

        try {
            const response = await api.post(
                `${process.env.REACT_APP_API_URL}/api/survey/vote?lang=${i18n.language}`,
                payload,
                {
                    headers: { Authorization: `Bearer ${accessToken}` },
                }
            );

            if (response.data.status === 'success') {
                Swal.fire({
                    text: t('survey.votesSubmitted') || response.data.message,
                    icon: 'success',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('survey.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
                await fetchSurveyData();
                setIsVoted(true);
            } else {
                Swal.fire({
                    text: t('survey.errorSubmittingVotes'),
                    icon: 'error',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#ff0000',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('survey.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            }
        } catch (error) {
            Swal.fire({
                text: t('survey.errorSubmittingVotes'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('survey.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
        } finally {
            setLoadingVote(false);
        }
    };

    // const handleLogin = () => {
    //     const redirectPath = window.location.pathname;
    //     setShowModal(true);
    //     localStorage.setItem('redirectPath', redirectPath);
    // };

    // const confirmLogin = () => {
    //     const redirectPath = localStorage.getItem('redirectPath') || '/';
    //     window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/steam?redirect=${encodeURIComponent(redirectPath)}`;
    //     setShowModal(false);
    // };

    const handleNext = () => {
        if (currentQuestionIndex < survey.questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    const handleOptionSelect = (questionId, optionId) => {
        setSelectedOptions(prev => ({ ...prev, [questionId]: optionId }));
    };

    if (!isAuthReady || isLoading) {
        return <LoadingSpinner />;
    }

    const inactiveSurveys = () => {
        navigate(`/surveys/inactive`);
    };

    const renderChart = (type, questionData, questionId) => {
        const xAxisProps = getXAxisProps(questionData);
        const isManyOptions = questionData.length > 5;

        const chartMargin = isManyOptions
            ? { top: 20, right: 30, left: 20, bottom: 70 }
            : { top: 20, right: 30, left: 20, bottom: 5 };

        switch (type) {
            case 'Area':
                return (
                    <AreaChart data={questionData} margin={chartMargin}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" {...xAxisProps} />
                        <YAxis domain={[0, questionId === 2 ? 20000 : 'auto']} />
                        <Tooltip cursor={{ fill: 'transparent' }} />
                        <Legend content={() => null} />
                        <Area
                            type="monotone"
                            dataKey="votes"
                            name={t('survey.votes')}
                            stroke="rgba(255, 0, 73, 0.9)"
                            fill="rgba(255, 0, 73, 0.4)"
                        />
                    </AreaChart>
                );
            default:
                return (
                    <BarChart data={questionData} margin={chartMargin}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" {...xAxisProps} />
                        <YAxis domain={[0, questionId === 2 ? 20000 : 'auto']} />
                        <Tooltip cursor={{ fill: 'transparent' }} />
                        <Legend content={() => null} />
                        <Bar
                            dataKey="votes"
                            name={t('survey.votes')}
                            fill="rgba(255, 0, 73, 0.6)"
                        />
                    </BarChart>
                );
        }
    };

    const isLastQuestion = currentQuestionIndex === survey.questions.length - 1;
    const progress = ((currentQuestionIndex + 1) / survey.questions.length) * 100;

    return (
        <div className="survey-page">
            {!isVoted && isAuthenticated ? (
                <div className="survey-container">
                    <div className="section-title">
                        <span className="title-text">{t('survey.title')}</span>
                        <span className="arrow-icon">›</span>
                    </div>
                    <div className="survey-wrapper no-top-border">
                        <aside className="survey-question-list">
                            <div className="survey-question-wrapper">
                                <div className="survey-progress">
                                    <span>{t('survey.question')} {currentQuestionIndex + 1} {t('survey.of')} {survey.questions.length}</span>
                                    <ProgressBar votesCount={currentQuestionIndex + 1} maxVotes={survey.questions.length} />
                                </div>
                                <div className="survey-question">
                                    <span>{survey.questions[currentQuestionIndex].question}</span>
                                    <div className="survey-options">
                                        {survey.questions[currentQuestionIndex].options.length > 4 ? (
                                            <select
                                                onChange={(e) =>
                                                    handleOptionSelect(
                                                        survey.questions[currentQuestionIndex].questionId,
                                                        parseInt(e.target.value, 10)
                                                    )
                                                }
                                                value={selectedOptions[survey.questions[currentQuestionIndex].questionId] || ''}
                                                className="survey-dropdown-menu"
                                            >
                                                <option value="" disabled>
                                                    {t('survey.selectOption')}
                                                </option>
                                                {survey.questions[currentQuestionIndex].options.map(opt => (
                                                    <option key={opt.id} value={opt.id}>
                                                        {opt.title}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            survey.questions[currentQuestionIndex].options.map(opt => (
                                                <div key={opt.id} className="survey-option">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name={`surveyOption-${survey.questions[currentQuestionIndex].questionId}`}
                                                            value={opt.id}
                                                            checked={
                                                                selectedOptions[survey.questions[currentQuestionIndex].questionId] === opt.id
                                                            }
                                                            onChange={() =>
                                                                handleOptionSelect(survey.questions[currentQuestionIndex].questionId, opt.id)
                                                            }
                                                        />
                                                        <p>{opt.title}</p>
                                                    </label>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                                <div className="navigation-buttons">
                                    <button
                                        onClick={handlePrevious}
                                        disabled={currentQuestionIndex === 0}
                                        className="nav-button"
                                    >
                                        {t('survey.previous')}
                                    </button>
                                    {isLastQuestion ? (
                                        <button
                                            onClick={handleVote}
                                            disabled={
                                                !selectedOptions[survey.questions[currentQuestionIndex].questionId] || loadingVote
                                            }
                                            className="vote-button"
                                        >
                                            {loadingVote ? t('survey.voting') : t('survey.submit')}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={handleNext}
                                            disabled={!selectedOptions[survey.questions[currentQuestionIndex].questionId]}
                                            className="nav-button"
                                        >
                                            {t('survey.next')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            ) : !isAuthenticated ? (
                <div className="survey-message">
                    <p>{t('survey.authMessage')}</p>
                    {/*<button className="login-button" onClick={handleLogin}>*/}
                    {/*    {t('messages.loginViaSteam')}*/}
                    {/*</button>*/}
                </div>
            ) : null}

            <div className="survey-container">
                <div className="section-title">
                    <span className="title-text">{t('survey.results')}</span>
                    <span className="arrow-icon">›</span>
                </div>
                <div className="survey-wrapper no-top-border">
                    <div className="survey-result">
                        <aside className="survey-list">
                            <div className="survey-type-container">
                                {chartTypes.map((type) => {
                                    const label = t(`survey.chartTypes.${type}`);
                                    const isActive = chartType === type;
                                    const buttonClass = isActive
                                        ? "survey-type-btn survey-type-btn-active"
                                        : "survey-type-btn";
                                    return (
                                        <button
                                            key={type}
                                            onClick={() => setChartType(type)}
                                            className={buttonClass}
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                                <div className="total-votes">
                                    {t('survey.totalVotes', { count: survey.totalVotes || 0 })}
                                </div>
                                <span className="previous-surveys-button" onClick={inactiveSurveys}>
                                    <FontAwesomeIcon className="" icon={faPoll} /> {t('survey.previousSurveys')}
                                    <FontAwesomeIcon className="fa-arrow-right" icon={faArrowRight} />
                                </span>
                            </div>
                            {survey.questions.map(question => {
                                const questionData = question.options.map(opt => ({
                                    name: opt.title,
                                    votes: opt.votesCount
                                }));
                                return (
                                    <div key={question.questionId} className="survey-question-result">
                                        <p>{question.question}</p>
                                        <ResponsiveContainer width="100%" height={300}>
                                            {renderChart(chartType, questionData, question.questionId)}
                                        </ResponsiveContainer>
                                    </div>
                                );
                            })}
                        </aside>
                    </div>
                    <SurveyComments
                        isAuthenticated={isAuthenticated}
                        isCommented={isCommented}
                        isVoted={isVoted}
                        surveyId={survey.surveyId}
                        token={accessToken}
                        currentUserId={userId}
                        isBanned={isBanned}
                        bannedEndDate={bannedEndDate}
                        isAdmin={isAdmin}
                    />
                </div>
            </div>

            {/*<Modal show={showModal} onHide={() => setShowModal(false)}>*/}
            {/*    <Modal.Header closeButton>*/}
            {/*        <Modal.Title>{t('auth.steamLoginTitle')}</Modal.Title>*/}
            {/*    </Modal.Header>*/}
            {/*    <Modal.Body>*/}
            {/*        <p>{t('auth.steamLoginMessage')}</p>*/}
            {/*    </Modal.Body>*/}
            {/*    <Modal.Footer>*/}
            {/*        <Button variant="secondary" onClick={() => setShowModal(false)}>*/}
            {/*            {t('auth.cancel')}*/}
            {/*        </Button>*/}
            {/*        <Button variant="primary" onClick={confirmLogin}>*/}
            {/*            {t('auth.proceed')}*/}
            {/*        </Button>*/}
            {/*    </Modal.Footer>*/}
            {/*</Modal>*/}
        </div>
    );
}

export default SurveyPage;
